import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, Compass, Sparkles, Loader2, MapPin, Clock, ArrowRight, Zap, Heart, AlertTriangle, Circle } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASPECT_STYLES = {
  positif: { color: '#4CAF50', icon: Heart, label: 'Harmonieux' },
  tension: { color: '#FF5722', icon: Zap, label: 'Dynamique' },
  neutre: { color: '#9E9E9E', icon: Circle, label: 'Neutre' }
};

const AspectCard = ({ aspect, index }) => {
  const style = ASPECT_STYLES[aspect.aspect?.nature] || ASPECT_STYLES.neutre;
  const Icon = style.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-sky-200 hover:border-gray-200 transition-all"
    >
      {/* Nature Icon */}
      <div 
        className="p-2 rounded-lg shrink-0"
        style={{ backgroundColor: `${style.color}20`, border: `1px solid ${style.color}40` }}
      >
        <Icon className="h-5 w-5" style={{ color: style.color }} />
      </div>
      
      {/* Planets */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900">{aspect.planet1?.name}</span>
          <span className="text-lg" style={{ color: aspect.aspect?.color }}>{aspect.aspect?.symbol}</span>
          <span className="font-semibold text-gray-900">{aspect.planet2?.name}</span>
        </div>
        <p className="text-sm text-gray-600">{aspect.aspect?.name}</p>
      </div>
      
      {/* Orb */}
      <div className="text-right shrink-0">
        <span className="text-xs text-gray-500">Orbe</span>
        <p className="text-sm text-gray-700">{aspect.orb}°</p>
      </div>
    </motion.div>
  );
};

const AspectSummary = ({ summary }) => {
  const total = summary?.total || 0;
  const harmonious = summary?.harmonious_count || 0;
  const challenging = summary?.challenging_count || 0;
  
  const harmoniousPercent = total > 0 ? Math.round((harmonious / total) * 100) : 50;
  const challengingPercent = total > 0 ? Math.round((challenging / total) * 100) : 50;
  
  return (
    <div className="bg-white rounded-2xl border border-sky-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Équilibre des Aspects</h3>
      
      {/* Balance Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-green-400 flex items-center gap-1"><Heart className="h-4 w-4" /> Harmonieux</span>
          <span className="text-orange-400 flex items-center gap-1"><Zap className="h-4 w-4" /> Dynamiques</span>
        </div>
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden flex">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${harmoniousPercent}%` }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-green-500 to-emerald-500"
          />
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${challengingPercent}%` }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-r from-orange-500 to-red-500"
          />
        </div>
        <div className="flex justify-between text-xs mt-1">
          <span className="text-gray-600">{harmonious} aspects</span>
          <span className="text-gray-600">{challenging} aspects</span>
        </div>
      </div>
      
      {/* Balance Interpretation */}
      <div className={`p-4 rounded-xl ${
        summary?.balance === 'harmonieux' ? 'bg-green-500/10 border border-green-500/30' :
        summary?.balance === 'dynamique' ? 'bg-orange-500/10 border border-orange-500/30' :
        'bg-sky-100 border border-sky-200'
      }`}>
        <p className="text-sm text-gray-700">
          {summary?.balance === 'harmonieux' && (
            <>
              <strong className="text-green-400">Thème harmonieux</strong> : Tes énergies circulent naturellement. 
              Attention à ne pas rester dans ta zone de confort.
            </>
          )}
          {summary?.balance === 'dynamique' && (
            <>
              <strong className="text-orange-400">Thème dynamique</strong> : Beaucoup de tensions créatives. 
              C'est un moteur d'évolution, pas un obstacle.
            </>
          )}
          {summary?.balance === 'équilibré' && (
            <>
              <strong className="text-sky-600">Thème équilibré</strong> : Bon mélange de fluidité et de défis.
              Tu as les ressources pour naviguer les obstacles.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

const AstrozodiacAspectsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/aspects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success('Aspects calculés !');
      } else {
        toast.error(data.detail || 'Erreur de calcul');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/aspects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chart_data: result,
          focus: 'aspects',
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  const getAspectsList = () => {
    if (!result?.aspects) return [];
    switch (activeTab) {
      case 'harmonious': return result.aspects.harmonious || [];
      case 'challenging': return result.aspects.challenging || [];
      default: return result.aspects.all || [];
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/astrozodiac" className="flex items-center gap-2 text-sky-600 hover:text-sky-500" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-rose-400" />
            <span className="font-bold text-gray-900">Mécanique Interne</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-rose-500/20 to-pink-500/20 border border-rose-200 mb-4">
                <Compass className="h-10 w-10 text-rose-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Ta Mécanique Interne</h1>
              <p className="text-gray-600">Les aspects révèlent tes tensions et fluidités intérieures</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-firstname" />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input type="date" value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthdate" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Heure (optionnel)</Label>
                <Input type="time" value={formData.birthTime} onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthtime" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville *</Label>
                <Input type="text" value={formData.birthCity} onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-city" />
              </div>
              
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-gray-900 font-semibold rounded-xl py-6"
                data-testid="calculate-btn">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Calcul...</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser mes aspects</>}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Aspects de {formData.firstName}</h2>
              <p className="text-gray-600">Les dialogues entre tes planètes</p>
            </div>
            
            {/* Summary */}
            <AspectSummary summary={result.summary} />
            
            {/* Tabs */}
            <div className="flex gap-2 justify-center">
              {[
                { id: 'all', label: 'Tous', count: result.aspects?.all?.length },
                { id: 'harmonious', label: 'Harmonieux', count: result.aspects?.harmonious?.length, color: 'green' },
                { id: 'challenging', label: 'Dynamiques', count: result.aspects?.challenging?.length, color: 'orange' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    px-4 py-2 rounded-xl text-sm font-medium transition-all
                    ${activeTab === tab.id 
                      ? `bg-${tab.color || 'cyan'}-500/20 text-${tab.color || 'cyan'}-400 border border-${tab.color || 'cyan'}-500/40` 
                      : 'bg-white text-gray-600 border border-sky-200 hover:border-gray-200'}
                  `}
                  style={activeTab === tab.id ? { 
                    backgroundColor: tab.color === 'green' ? 'rgba(74, 222, 128, 0.1)' : tab.color === 'orange' ? 'rgba(251, 146, 60, 0.1)' : 'rgba(34, 211, 238, 0.1)',
                    color: tab.color === 'green' ? '#4ade80' : tab.color === 'orange' ? '#fb923c' : '#22d3ee',
                    borderColor: tab.color === 'green' ? 'rgba(74, 222, 128, 0.4)' : tab.color === 'orange' ? 'rgba(251, 146, 60, 0.4)' : 'rgba(34, 211, 238, 0.4)'
                  } : {}}
                >
                  {tab.label} ({tab.count || 0})
                </button>
              ))}
            </div>
            
            {/* Aspects List */}
            <div className="space-y-3">
              {getAspectsList().map((aspect, index) => (
                <AspectCard key={index} aspect={aspect} index={index} />
              ))}
              {getAspectsList().length === 0 && (
                <p className="text-center text-gray-600 py-8">Aucun aspect dans cette catégorie</p>
              )}
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-600" /> Interprétation IA
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none"><ReactMarkdown>{interpretation}</ReactMarkdown></div>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={getInterpretation} disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 text-gray-900 rounded-xl" data-testid="get-interpretation-btn">
                    {loadingInterpretation ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Obtenir l'interprétation IA</>}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" onClick={() => { setResult(null); setInterpretation(null); }}
                className="border-gray-200 text-gray-700 hover:bg-white rounded-xl" data-testid="new-calc-btn">
                Nouveau calcul
              </Button>
              <Button onClick={() => navigate('/astrozodiac')}
                className="bg-gradient-to-r from-sky-500 to-cyan-500 text-gray-900 rounded-xl" data-testid="go-home-btn">
                Retour à l'accueil <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacAspectsPage;
